<template>
  <div>
    <div class="d-flex align-center">
      <div class="img-preview mr-4">
        <v-skeleton-loader v-if="isLoading" :height="height" :width="width" type="card"></v-skeleton-loader>
        <div v-else>
          <div v-if="mediaUrl && mediaType === 'pdf'" id="pdf-upload-image"></div>
          <div v-else-if="mediaUrl && mediaType === 'video' && video">
            <div class="mb-n6">
              <div @click="video.show = true" class="black video-thumbnail">
                <img :src="video.thumbnailUrl || '/default-img.png'" />
                <div class="play-icon-wrapper">
                  <v-icon color="info" large class="play-icon"> mdi-play </v-icon>
                </div>
              </div>
              <v-checkbox v-model="video.loop" label="Loop Video" color="black" class="mb-n8"></v-checkbox>
              <v-checkbox v-model="video.autoplay" label="Autoplay Video" color="black"></v-checkbox>
            </div>

            <v-dialog
              v-model="video.show"
              transition="dialog-bottom-transition"
              width="fit-content"
              @blur="videoClose"
              eager
            >
              <div class="black d-flex flex-column">
                <v-toolbar dark color="black">
                  <v-btn icon @click="video.show = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  <v-toolbar-title>{{ video.name }}</v-toolbar-title>
                  <v-spacer></v-spacer>
                </v-toolbar>
                <video id="video-upload" :src="mediaUrl" controls></video>
              </div>
            </v-dialog>
          </div>
          <v-img
            v-else
            max-height="100"
            max-width="100"
            :src="mediaUrl || '/default-img.png'"
            class="rounded-sm"
          ></v-img>
        </div>
      </div>

      <div style="align-self: flex-start">
        <div>
          <v-file-input
            :disabled="isLoading"
            accept="image/*,.pdf,video/*"
            v-model="file"
            class="file-input"
            :rules="rules"
            ref="myFile"
            @change="selectedFile"
            @click="$event.target.value = null"
            truncate-length="15"
          ></v-file-input>

          <v-btn
            :disabled="isLoading"
            large
            depressed
            color="grey lighten-4"
            class="mr-4 text-capitalize font-weight-bold black--text"
            height="36"
            @click="triggerFileInput"
          >
            <span class="mr-2">Choose File</span>
          </v-btn>

          <v-btn
            :disabled="isLoading"
            large
            depressed
            color="grey lighten-4"
            class="text-capitalize font-weight-bold black--text"
            height="36"
            @click="clearFile()"
          >
            <span class="mr-2">Clear</span>
          </v-btn>
        </div>

        <p v-if="showErrorMessage" class="error--text mt-2" style="margin: 0; max-width: 340px">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue, { PropType } from 'vue';
  import { fileDelete, fileUpload } from '@/utils/aws';
  import { mapActions } from 'vuex';
  import PDFObject from 'pdfobject';
  import { Media, MediaPosition, MediaType } from '../types';
  import { ImageExts, VideoExts } from '@/constants';

  const IMAGE_MAX_MB = 10;
  const VIDEO_MAX_MB = 50;

  export default Vue.extend({
    name: 'Upload',
    props: {
      clientId: {
        type: Number,
        required: false,
      },
      value: {
        type: Object as PropType<Media>,
        default: null,
      },
      rules: {
        type: Array,
      },
      errorMessage: {
        type: String,
        default: 'Error',
        required: false,
      },
      showErrorMessage: {
        type: Boolean,
        default: false,
        required: false,
      },
    },
    data: function () {
      var file: File = null;
      var video: { url: string; name: string; thumbnailUrl: string; show: boolean; loop: boolean; autoplay: boolean } =
        null;
      return {
        mediaUrl: this.value.url || '',
        file,
        isLoading: false,
        video,
        height: 60,
        width: 60,
      };
    },
    watch: {
      'value.url': {
        handler(val: string) {
          const url = val ?? '';

          if (this.mediaType === 'pdf') {
            this.$nextTick().then(() => {
              PDFObject.embed(url, '#pdf-upload-image', { forceIframe: true, pdfOpenParams: { toolbar: 1 } });
            });
          } else if (this.mediaType === 'video') {
            this.video = {
              url,
              name: url.split('/').pop(),
              thumbnailUrl: this.value.videoTypeDetails.thumbnailUrl,
              show: false,
              loop: this.value.videoTypeDetails.shouldLoop,
              autoplay: this.value.videoTypeDetails.shouldAutoplay,
            };
          }
        },
        immediate: true,
      },
      'video.show'(isShowing) {
        const video = document.getElementById('video-upload') as HTMLVideoElement;

        if (!video) return;

        if (isShowing) {
          if (this.video.autoplay) {
            video.play();
          }
          video.loop = this.video.loop;
        } else {
          video.pause();
          video.currentTime = 0;
        }
      },
      'video.loop'(val) {
        if (val == null) return;

        this.$emit('input', {
          videoTypeDetails: { ...this.value.videoTypeDetails, shouldLoop: val },
        } as Partial<Media>);
      },
      'video.autoplay'(val) {
        if (val == null) return;

        this.$emit('input', {
          videoTypeDetails: { ...this.value.videoTypeDetails, shouldAutoplay: val },
        } as Partial<Media>);
      },
    },

    computed: {
      s3Folder(): string {
        return `digital_programs/${this.clientId || 0}/images`;
      },
      mediaType(): MediaType {
        if (VideoExts.includes(this.mediaUrl.split('.').pop())) {
          return 'video';
        }
        if (ImageExts.includes(this.mediaUrl.split('.').pop())) {
          return 'image';
        } else if (this.mediaUrl.split('.').pop().includes('pdf')) {
          return 'pdf';
        }
        return '';
      },
    },
    methods: {
      ...mapActions('ui', { showToast: 'showToast' }),

      videoClose() {
        (document.getElementById('video-upload') as HTMLVideoElement).currentTime = 0;
      },
      generateThumbnailFile(file: File) {
        return new Promise<File>((resolve, reject) => {
          const video = document.createElement('video');
          video.src = URL.createObjectURL(file);
          video.currentTime = 1;

          video.addEventListener('loadeddata', () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Failed to create thumbnail blob.'));
                return;
              }
              const ext = file.name.split('.').pop();
              const name = file.name.replace(`.${ext}`, '');
              const thumbnailFile = new File([blob], `${name}-thumbnail`, { type: 'image/png' });
              resolve(thumbnailFile);
            });
          });
        });
      },
      selectedFile() {
        let file: File = this.file;
        const ext = file.name.split('.').pop();
        if (![...VideoExts, ...ImageExts, 'pdf'].includes(ext)) {
          this.showToast(`Invalid file type`);
          return false;
        }
        if (!file.type.includes('video')) {
          if (file.size / 1024 ** 2 > IMAGE_MAX_MB) {
            this.showToast(`The image size must be lower than ${IMAGE_MAX_MB}mb`);
            return false;
          }
        } else {
          if (file.size / 1024 ** 2 > VIDEO_MAX_MB) {
            this.showToast(`The video size must be lower than ${VIDEO_MAX_MB}mb`);
            return false;
          }
        }

        this.uploadFile(file);
      },
      async uploadFile(file: File) {
        try {
          this.isLoading = true;
          this.$emit('loading-image', true);

          const result = await fileUpload(file, this.s3Folder, this.clientId);

          if (result.status !== 200) {
            throw new Error('Something went wrong');
          } else {
            this.mediaUrl = result.file;

            const mediaDetails: Media = {
              url: this.mediaUrl,
              position: MediaPosition.Center,
              videoTypeDetails: null,
              imageTypeDetails: null,
            };
            if (file.type.includes('video')) {
              const thumbnailFile = await this.generateThumbnailFile(file);
              const thumbnailResult = await fileUpload(thumbnailFile, this.s3Folder, this.clientId);
              mediaDetails.videoTypeDetails = {
                thumbnailUrl: thumbnailResult.file,
                shouldAutoplay: true,
                shouldLoop: true,
              };
            } else if (file.type.includes('image')) {
              mediaDetails.imageTypeDetails = {
                redirectLink: '',
              };
            }
            this.$emit('input', mediaDetails);
          }
        } catch (e) {
          console.log(e);
          this.showToast(e.message);
        } finally {
          this.isLoading = false;
          this.$emit('loading-image', false);
        }
      },
      triggerFileInput() {
        ((this.$refs.myFile as Vue).$el.querySelector('input[type="file"]') as HTMLInputElement).click();
      },
      clearFile() {
        this.mediaUrl = '';
        this.video = null;
        this.$emit('input', { url: '', imageTypeDetails: null, videoTypeDetails: null } as Media);
        this.$emit('validateForm');
        fileDelete(this.value.url, this.clientId);
      },
    },
  });
</script>
<style lang="sass" scoped>
  input[type='file']
    display: none

  .custom-file-upload
    display: inline-block
    padding: 6px 12px
    cursor: pointer
    font-size: 0.875rem
    white-space: nowrap

  .img-dimensions
    width: 380px
    height: 41px

  .file-input
    display: none

  #pdf
    border: 1px solid gray
    min-height: 255px // min-height to show toolbar
    min-width: 425px // min-width to show toolbar
    display: grid

  .video-thumbnail
    cursor: pointer
    position: relative
    display: flex
    justify-content: center
    img
      display: block
      max-width: 20em
      max-height: 10em
    .play-icon-wrapper
      background-color: rgba(33, 33, 33, 0.6);
      position: absolute
      width: 100%
      height: 100%
      top: 0
      left: 0
      .play-icon
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)
  #video-upload
    display: block
    max-width: 90vw
    max-height: 80vh

  video::-webkit-media-controls-timeline,
  video::-webkit-media-controls-current-time-display,
  video::-webkit-media-controls-time-remaining-display
    display: none
</style>
